import { IftRoom } from "@/Pages/Cases/types/IftRoom";
import { defineStore } from "pinia";

import type { CaseExtras, FileType, UpdateIFTDatesParams } from "@/App/types/twiageTypes";
import { FeatureFlag, TimelineFilterType } from "@/App/types/twiageTypes";
import type { DataState } from "@/Pages/utils/DataState";
import { InitialState, Loading, LoadingFailed, LoadingSucceeded } from "@/Pages/utils/DataState";
import { isCaseMatchingMainFilters } from "@/Pages/Cases/functions";
import { CasesService } from "@/Pages/Cases/services";
import { IdComparator, updateInList } from "@/App/utils/updateInList";
import type { StateId } from "@/Pages/Cases/CaseAction";
import { CaseAction, HospitalCaseAction } from "@/Pages/Cases/CaseAction";
import { CasesQuickFilter } from "@/Pages/Cases/CasesQuickFilter";
import { CasesView } from "@/Pages/Cases/CasesView";
import storage from "@/App/services/storage.service";
import type { UserToInvite } from "@/App/components/InviteUsersDropdown/UserToInvite";
import userService from "@/Pages/Login/services/user.service";
import { emptyDashboardFilters, filtersService } from "@/Pages/Cases/filtering/FiltersService";
import type { TwiageRequestError } from "@/App/services/httpClient/HttpClient";
import { forbiddenError, TwiageErrorCode } from "@/App/services/httpClient/HttpClient";
import type { DashboardFilters } from "@/Pages/Cases/filtering/DashboardFilters";
import { CaseMode, CaseModeParams } from "@/App/types/CaseMode";
import { useNavbarStore } from "@/App/components/AppNavbar/navigation.store";
import { getDateDaysAgo } from "@/Pages/Cases/functions/dateDaysAgo";
import { addDays, startOfDay } from "date-fns";
import { updateCaseInTheList } from "@/Pages/Cases/store/updateCaseInTheList";
import { updateProtocolState } from "@/Pages/Cases/store/updateProtocolState";
import { filterCase, filterCases } from "@/Pages/Cases/store/filterCases";
import { CaseType } from "@/App/types/caseType";
import { CaseListType } from "@/App/options/options.store";
import {
  addFilter,
  isFilterEnabled,
  isFilterOfGroupEnabled,
  removeFilter,
  removeFilterGroup,
} from "@/Pages/Cases/filtering/FilterManager";
import type { TwiageCase } from "@/App/types/TwiageCase";
import { quickFilterToFilter } from "@/Pages/Cases/filtering/filters/quickFilterToFilter";
import { CaseFilterGroup } from "@/Pages/Cases/filtering/CaseFilterGroup";
import type { CasesStoreState } from "@/Pages/Cases/CasesStoreState";
import { getMainFilters } from "@/Pages/Cases/filtering/getMainFilters";
import { useUserStore } from "@/App/user/user.store";
import { useOptionsStore } from "@/App/stores";
import type { UpdateEventParams } from "@/App/components/Dispatch/DispatchEvents/dispatchEvent";
import { SortBy, sortCases } from "@/Pages/Cases/sortCases";
import { type GroupBy, groupCases } from "@/Pages/Cases/groupCases";
import { getCreateCaseAction } from "@/Pages/Cases/createCaseAction";
import type { CaseFormUIFields } from "@/Pages/Cases/components/Forms/common/CaseFormUIFields";
import { handleDataEnabledTags } from "@/Pages/Cases/functions/tags/handleDataEnabledTags";
import { isMihCompletedCase } from "@/Pages/Cases/isMihCompletedCase";
import type { ActivatedProtocolResponse } from "./ActivatedProtocolResponse";
import type { EDFeedback } from "./components/CaseDetail/HandoffFeedback/EDFeedback";
import { createEventUpdateRequestBody } from "@/App/components/Dispatch/DispatchEvents/closure-events-constants";

export const confirmationModalId = "confirmationModal";

const viewsStorageKey = (): string => {
  return `cases_view_for_user_${userService.getUserId()}`;
};

const getCasesView = (): CasesView => {
  return storage.getItemAsObject(viewsStorageKey()) ?? CasesView.AllRecentCases;
};

const state = (): CasesStoreState => {
  const defaultState: CasesStoreState = {
    caseListUpdateCount: 0,
    cases: [],
    hospitalCaseRequestState: new InitialState(),
    filters: filtersService.getFilters(),
    quickFilters: [],
    mainFilters: [],
    casesView: getCasesView(),
    selectedCase: null,
    selectedCaseId: null,
    timelineFilter: TimelineFilterType.All,
    casesStates: new Map<string, Map<string, DataState<unknown>>>(),
    mode: CaseMode.ed,
    showCasesForDay: undefined,
    confirmationModal: { isModalShown: false, onSave: () => undefined },
  };
  defaultState.mainFilters = getMainFilters(defaultState);
  return defaultState;
};

export const useCasesStore = defineStore("cases", {
  actions: {
    async processCaseAction<T>({
      stateId,
      operation,
      verboseError,
    }: {
      stateId: StateId;
      operation: () => Promise<T>;
      verboseError: string;
    }): Promise<T | null> {
      this.setCaseState({ stateId, state: new Loading() });
      try {
        const result = await operation();
        this.setCaseState({ stateId, state: new LoadingSucceeded({ data: result }) });
        return result;
      } catch (error) {
        this.setCaseState({
          stateId,
          state: new LoadingFailed((error as TwiageRequestError).message as string, verboseError),
        });
      }
      return null;
    },
    setMode(mode: CaseMode): void {
      if (mode === this.mode) return;
      if (CaseModeParams[mode].calendarAvailable) {
        this.showCasesForDay = new Date();
      } else {
        this.showCasesForDay = undefined;
      }
      this.cases = [];
      this.resetQuickFilters();
      this.mode = mode;
      useNavbarStore().color = CaseModeParams[mode].appbarColor;
      this.synchronizeMainFilters();
    },
    synchronizeMainFilters(): void {
      this.mainFilters = getMainFilters(this);
    },
    changeDateBy({ days }: { days: number }): void {
      this.showCasesForDay = addDays(this.showCasesForDay ?? new Date(), days);
      this.cases = [];
      this.synchronizeMainFilters();
      this.getCases();
    },
    getState<T>(stateId: StateId): DataState<T> {
      return this.casesStates.get(stateIdToString(stateId)) ?? new InitialState();
    },
    isLoading(stateId: StateId): boolean {
      return this.getState(stateId) instanceof Loading;
    },
    setCaseState<T>({ stateId, state }: { stateId: StateId; state: DataState<T> }): void {
      this.casesStates.set(stateIdToString(stateId), state);
      if (stateId.actionId) {
        this.casesStates.set(stateIdToString({ caseId: stateId.caseId, action: stateId.action }), state);
      }
    },
    calculateDateRange(): Record<string, string> {
      if (this.casesView === CasesView.Archived) {
        return {
          [CaseModeParams[this.mode].dateFrom]: getDateDaysAgo(5),
          [CaseModeParams[this.mode].dateTo]: getDateDaysAgo(1),
        };
      }
      if (this.showCasesForDay) {
        return {
          [CaseModeParams[this.mode].dateFrom]: startOfDay(this.showCasesForDay).toISOString(),
          [CaseModeParams[this.mode].dateTo]: addDays(startOfDay(this.showCasesForDay), 1).toISOString(),
        };
      }
      return {};
    },
    getCases(): Promise<TwiageCase[] | null> {
      return this.processCaseAction({
        stateId: { action: CaseAction.retrievingCasesList },
        operation: async () => {
          const types = [...CaseModeParams[this.mode].types];

          if (useUserStore().isFeatureEnabled(FeatureFlag.showEdCasesMihDashboard) && types.includes(CaseType.mih))
            types.push(CaseType.ems);
          const updatedCases: TwiageCase[] = await CasesService.getCases({
            types,
            dateRangeOptions: this.calculateDateRange(),
          });

          if (this.selectedCaseId) {
            // preserving selected case since it contains more fields than we receive with cases list endpoint
            const selectedCaseInList: TwiageCase | undefined = updatedCases?.find(
              (aCase) => aCase.id === this.selectedCaseId,
            );
            if (
              (selectedCaseInList &&
                this.selectedCase &&
                selectedCaseInList.updatedAt !== this.selectedCase.updatedAt) ||
              (selectedCaseInList === undefined && this.selectedCase)
            ) {
              this.selectedCase = await this.getCase({
                caseId: this.selectedCaseId,
                updateCaseList: false,
              });
            }
            if (this.selectedCase) {
              updateInList({
                comparator: IdComparator,
                list: updatedCases,
                updatedItem: this.selectedCase,
              });
            }
          }
          this.cases = updatedCases;
          this.caseListUpdateCount++;
          return updatedCases;
        },
        verboseError: this.cases.length > 0 ? "Unable to refresh cases list" : "Unable to retrieve cases list",
      });
    },
    getCase({
      caseId,
      updateCaseList = true,
    }: {
      caseId: string;
      updateCaseList?: boolean;
    }): Promise<TwiageCase | null> {
      return this.processCaseAction({
        stateId: { caseId, action: CaseAction.retrievingCase },
        operation: async () => {
          try {
            const twiageCase = await CasesService.getCaseById({ caseId });
            if (this.selectedCaseId === twiageCase.id) {
              this.selectedCase = twiageCase;
            }
            if (updateCaseList) {
              updateInList({
                list: this.cases,
                updatedItem: twiageCase,
                comparator: IdComparator,
                addToList: true,
              });
              this.caseListUpdateCount++;
            }
            return twiageCase;
          } catch (error) {
            const castedError = error as TwiageRequestError;
            if (castedError?.code === TwiageErrorCode.forbidden) {
              this.selectedCase = null;
              this.cases = this.cases.filter((twiageCase) => twiageCase.id !== caseId);
              castedError.message = forbiddenError({ resourceName: "case" });
            }
            throw castedError;
          }
        },
        verboseError: `Unable to retrieve case with id ${caseId}`,
      });
    },

    onSessionStarts() {
      this.filters = filtersService.getFilters();
      this.synchronizeMainFilters();
    },

    createCase({
      payload,
      complaintSet,
    }: {
      payload: CaseFormUIFields;
      complaintSet: string;
    }): Promise<TwiageCase | null> {
      return this.processCaseAction({
        stateId: {
          action: getCreateCaseAction({ payload }),
        },
        operation: async () => {
          const createdCase = await CasesService.createCase({ payload, complaintSet });
          updateInList({
            addToList: true,
            list: this.cases,
            updatedItem: createdCase,
            comparator: IdComparator,
          });
          this.caseListUpdateCount++;
          this.getCase({ caseId: createdCase.id });
          return createdCase;
        },
        verboseError: "Unable to create case",
      });
    },

    editCase({ caseId, payload }: { caseId: string; payload: CaseFormUIFields }): Promise<TwiageCase | null> {
      return this.processCaseAction({
        stateId: { action: HospitalCaseAction.editCase },
        operation: async () => {
          if (payload.dataEnabledTags) handleDataEnabledTags(payload, this.selectedCase);
          const editedCase = await CasesService.editCase({ caseId, payload });
          this.getCase({ caseId: editedCase.id });
          return editedCase;
        },
        verboseError: "Unable to edit case",
      });
    },

    isQuickFilterApplied(filter: CasesQuickFilter): boolean {
      return isFilterEnabled({ filters: this.quickFilters, filterToCheck: quickFilterToFilter(filter) });
    },

    noQuickFiltersApplied(): boolean {
      return !isFilterOfGroupEnabled({ filters: this.quickFilters, group: CaseFilterGroup.quickFilter });
    },

    resetQuickFilters() {
      this.quickFilters = removeFilterGroup({ filters: this.quickFilters, group: CaseFilterGroup.quickFilter });
    },

    setQuickFilter(quickFilter: CasesQuickFilter) {
      const filters = quickFilterToFilter(quickFilter);
      if (this.isQuickFilterApplied(quickFilter)) {
        this.quickFilters = removeFilter({ filters: this.quickFilters, filterToRemove: filters });
      } else {
        this.quickFilters = addFilter({ filters: this.quickFilters, filterToAdd: filters });
      }
    },

    setCasesView(view: CasesView) {
      const previousCasesView = this.casesView;
      this.casesView = view;
      if ((view === CasesView.Archived || previousCasesView === CasesView.Archived) && previousCasesView !== view) {
        this.cases = [];
        this.getCases();
      }
      storage.save(viewsStorageKey(), this.casesView);
      this.synchronizeMainFilters();
    },

    setFilters({ newFilters }: { newFilters: DashboardFilters; clear?: boolean }) {
      filtersService.saveFilters(newFilters);
      this.filters = newFilters;
      this.synchronizeMainFilters();
    },

    clearMainFilters(): void {
      this.setFilters({ newFilters: emptyDashboardFilters() });
    },

    setTimelineFilter(filter: TimelineFilterType) {
      this.timelineFilter = filter;
    },

    updateRoom({ caseId, room }: { caseId: string; room: string }) {
      this.processCaseAction({
        stateId: { caseId, action: CaseAction.assignRoom },
        operation: () => CasesService.updateRoom({ caseId, room }),
        verboseError: "Failed to assign room.",
      });
    },

    updateIftRoom({ caseId, iftRoom, room }: { caseId: string; iftRoom: IftRoom; room: string }) {
      this.processCaseAction({
        stateId: { caseId, action: CaseAction.assignRoom },
        operation: () => CasesService.updateIftRoom({ caseId, iftRoom, room }),
        verboseError: "Failed to assign IFT room.",
      });
    },

    updateExtras({ caseId, extras }: { caseId: string; extras?: CaseExtras }) {
      this.processCaseAction({
        stateId: { caseId, action: CaseAction.updateExtras },
        operation: () => CasesService.updateExtras({ caseId, extras }),
        verboseError: "Failed to update extras.",
      });
    },

    updateSelectedCase({ update }: { update: Partial<TwiageCase> }): void {
      if (this.selectedCase && this.selectedCase.id === update.id) {
        this.selectedCase = {
          ...this.selectedCase,
          ...update,
        };
      }
    },

    markAsArrived({ caseId, caseCreatedAt }: { caseId: string; caseCreatedAt: string }): Promise<void> {
      return this.processCaseAction({
        stateId: { caseId, action: CaseAction.markArrived },
        operation: () => CasesService.modifyCase(caseId, { markedArrivedAt: new Date().toISOString() }),
        verboseError: "Failed to mark case as arrived.",
      }).then((updatedCase) => {
        if (updatedCase) {
          const update = { id: updatedCase.id, markedArrivedAt: updatedCase.markedArrivedAt };

          updateCaseInTheList({
            cases: this.cases,
            caseId,
            getCaseUpdate: () => update,
          });
          this.caseListUpdateCount++;

          this.updateSelectedCase({ update });
        }

        const userStore = useUserStore();

        if (userStore.isFeatureEnabled(FeatureFlag.printOnMarkAsArrived)) {
          CasesService.openPrintCasePDFDialog({ caseId, caseCreatedAt });
        }

        if (this.casesView == CasesView.NextArriving) {
          this.setSelectedCaseId({ caseId: null });
        }
      });
    },

    updateIftPatientTransportDates(params: UpdateIFTDatesParams): void {
      this.processCaseAction({
        stateId: { caseId: params.caseId, action: params.action },
        operation: () => CasesService.updateIFTDates(params),
        verboseError: "Failed to mark case as patient pickup.",
      }).then((updatedCase) => {
        if (updatedCase) {
          updateCaseInTheList({
            cases: this.cases,
            caseId: params.caseId,
            getCaseUpdate: () => ({
              ift: updatedCase.ift,
            }),
          });
        }
      });
    },

    updateEvents({ caseId, type, event }: UpdateEventParams): Promise<TwiageCase | null> {
      if (this.mode === CaseMode.ift || this.mode === CaseMode.mih) {
        return this.processCaseAction({
          stateId: { caseId, action: CaseAction.updateIftCaseEvent },
          operation: async () =>
            await CasesService.updateEvent({
              caseId,
              ...createEventUpdateRequestBody(event, type),
              mode: this.mode,
            }),
          verboseError: "Failed to update case event.",
        });
      }
      return Promise.resolve(null);
    },

    setAsLeftED({ caseId }: { caseId: string }): void {
      this.processCaseAction({
        stateId: { caseId, action: CaseAction.setAsLeftED },
        operation: () => CasesService.modifyCase(caseId, { leftED: new Date() }),
        verboseError: 'Failed to set case as "left the ED."',
      }).then((updatedCase) => {
        if (updatedCase) {
          const update = { id: updatedCase.id, leftED: updatedCase.leftED };

          updateCaseInTheList({
            cases: this.cases,
            caseId,
            getCaseUpdate: () => ({ leftED: updatedCase.leftED }),
          });
          this.caseListUpdateCount++;

          this.updateSelectedCase({ update });
        }
      });
    },

    setSelectedCaseId({ caseId }: { caseId: string | null }): void {
      this.selectedCase = null;
      this.selectedCaseId = caseId;
      this.timelineFilter = TimelineFilterType.All;
      if (caseId !== null) {
        this.getCase({ caseId });
      }
    },

    async sendMessage({ caseId, text, action }: { caseId: string; text: string; action: CaseAction }) {
      await this.processCaseAction({
        stateId: { caseId, action },
        operation: () => CasesService.sendChatMessage({ caseId, message: text }),
        verboseError: "Failed to send chat message",
      }).then((newMessage) => {
        if (newMessage) {
          updateCaseInTheList({
            cases: this.cases,
            caseId,
            getCaseUpdate: (caseToUpdate) => ({
              messages: [...(caseToUpdate.messages ?? []), newMessage],
            }),
          });
          this.caseListUpdateCount++;
        }
      });
    },

    activateProtocol({
      caseId,
      protocolId,
    }: {
      caseId: string;
      protocolId: string;
    }): Promise<ActivatedProtocolResponse | void> {
      return this.processCaseAction({
        stateId: { caseId, action: CaseAction.activateProtocol, actionId: protocolId },
        operation: () => CasesService.activateProtocol({ caseId, protocolId }),
        verboseError: "Failed to activate protocol.",
      }).then((result) => {
        if (result) {
          updateProtocolState({ cases: this.cases, caseId, result });
          this.caseListUpdateCount++;
        }
      });
    },

    recallProtocol({ caseId, protocolId }: { caseId: string; protocolId: string }): void {
      this.processCaseAction({
        stateId: {
          caseId,
          action: CaseAction.recallProtocol,
          actionId: protocolId,
        },
        operation: () => CasesService.recallProtocol({ caseId, protocolId }),
        verboseError: "Failed to recall protocol.",
      }).then((result) => {
        updateProtocolState({ cases: this.cases, caseId, result });
        this.caseListUpdateCount++;
      });
    },

    activateProtocolStep({ caseId, protocolId, stepId }: { caseId: string; protocolId: string; stepId: string }): void {
      this.processCaseAction({
        stateId: {
          caseId,
          action: CaseAction.activateProtocolStep,
          actionId: stepId,
        },
        operation: () => CasesService.activateProtocolStep({ caseId, protocolId, stepId }),
        verboseError: "Failed to activate protocol step.",
      }).then((result) => {
        updateProtocolState({ cases: this.cases, caseId, result });
      });
    },

    recallProtocolStep({ caseId, protocolId, stepId }: { caseId: string; protocolId: string; stepId: string }): void {
      this.processCaseAction({
        stateId: {
          caseId,
          action: CaseAction.recallProtocolStep,
          actionId: stepId,
        },
        operation: () => CasesService.recallProtocolStep({ caseId, protocolId, stepId }),
        verboseError: "Failed to recall protocol step.",
      }).then((result) => {
        updateProtocolState({ cases: this.cases, caseId, result });
      });
    },

    inviteUsers({ caseId, userIds }: { caseId: string; userIds: Array<string> }): Promise<Array<UserToInvite> | null> {
      return this.processCaseAction({
        stateId: { caseId, action: CaseAction.invite },
        operation: () => CasesService.inviteUsers({ caseId, userIds }),
        verboseError: "Failed to invite users.",
      });
    },

    getStatUsersToInvite({ caseId }: { caseId: string }): Promise<Array<UserToInvite> | null> {
      return this.processCaseAction({
        stateId: { caseId, action: CaseAction.gettingStatUsersToInvite },
        operation: () => CasesService.getUsersToInvite({ caseId }),
        verboseError: "Failed to get users list to invite to the case.",
      });
    },

    downloadCaseReport({ caseId, caseCreatedAt }: { caseId: string; caseCreatedAt: string }): Promise<string | null> {
      return this.processCaseAction({
        stateId: { caseId, action: CaseAction.report },
        operation: () => CasesService.caseReport({ caseId, caseCreatedAt }),
        verboseError: "Failed to produce case report.",
      });
    },

    loadMedia({ caseId, uploadId, fileType }: { caseId: string; uploadId: string; fileType: FileType }): void {
      this.processCaseAction({
        stateId: { caseId, action: CaseAction.downloadMedia, actionId: uploadId },
        operation: () => CasesService.downloadFile({ caseId, uploadId, fileType }),
        verboseError: `Failed to download media for ${caseId} upload id: ${uploadId}`,
      });
    },

    createHandoffFeedback({ caseId, feedback }: { caseId: string; feedback: EDFeedback }) {
      return this.processCaseAction({
        stateId: { caseId, action: CaseAction.createHandoffFeedback },
        operation: () => CasesService.createHandoffFeedback({ caseId, feedback }),
        verboseError: "Failed to create handoff feedback.",
      });
    },

    onFormClosed({ caseFormAction }: { caseFormAction: HospitalCaseAction }) {
      this.setCaseState({ stateId: { action: caseFormAction }, state: new InitialState() });
    },

    onSessionEnd(): void {
      this.cases = [];
      this.selectedCase = null;
      this.selectedCaseId = null;
      this.filters = emptyDashboardFilters();
      this.casesStates = new Map<string, DataState<unknown>>();
    },
  },
  getters: {
    rawCasesList: (state: CasesStoreState): TwiageCase[] => {
      return state.cases;
    },
    casesListAfterApplyingMainFilters: (state: CasesStoreState): TwiageCase[] => {
      return state.cases.filter((twiageCase: TwiageCase) => isCaseMatchingMainFilters(twiageCase, state.mainFilters));
    },
    casesCountForFilter() {
      return (filter: CasesQuickFilter): number =>
        this.casesListAfterApplyingMainFilters.filter((twiageCase) =>
          filterCase({ twiageCase, filters: [quickFilterToFilter(filter)] }),
        ).length;
    },
    casesAfterApplyingQuickFilters(state: CasesStoreState): TwiageCase[] {
      const casesList = filterCases({
        twiageCases: this.casesListAfterApplyingMainFilters,
        filters: state.quickFilters,
      });
      let sortBy: SortBy | undefined;
      if (this.casesView === CasesView.NextArriving) {
        sortBy = SortBy.eta;
      }
      if (this.mode === CaseMode.ift) {
        sortBy = SortBy.expectedPickupTime;
      }

      let groupBy: GroupBy<TwiageCase> | undefined;
      if (useOptionsStore().groupCaseListByHospital) {
        groupBy = (twCase: TwiageCase): string => twCase?.destinationHospital?.name ?? "";
      }
      const groups = groupCases(casesList, groupBy);
      return Object.keys(groups)
        .sort((a: string, b: string): number => a.localeCompare(b)) // sort keys alphabetically
        .map((key) => groups[key].sort(sortCases({ sortBy })))
        .flat();
    },
    casesCount(): number {
      return this.casesListAfterApplyingMainFilters.length;
    },
    showCalendar(state: CasesStoreState): boolean {
      return CaseModeParams[state.mode].calendarAvailable && useOptionsStore().caseListType !== CaseListType.Map;
    },
    EDCasesForMapView(): TwiageCase[] {
      return this.casesAfterApplyingQuickFilters.filter(
        (twiageCase: TwiageCase) =>
          twiageCase.locationUpdates?.length &&
          !twiageCase.arrivedAt &&
          !twiageCase.markedArrivedAt &&
          twiageCase.eta !== 0 &&
          twiageCase.eta !== -1,
      );
    },
    IFTCasesForMapView(): TwiageCase[] {
      return this.casesAfterApplyingQuickFilters.filter(
        (twiageCase: TwiageCase) =>
          filterCase({ twiageCase, filters: [quickFilterToFilter(CasesQuickFilter.IftOpen)] }) &&
          !twiageCase.ift?.deliveredAt,
      );
    },
    MIHCasesForMapView(): TwiageCase[] {
      return this.casesAfterApplyingQuickFilters.filter(
        (twiageCase: TwiageCase) => twiageCase.type === CaseType.mih && twiageCase.mih?.location?.coordinates,
      );
    },
    mihAssignedCases(): TwiageCase[] {
      return this.casesAfterApplyingQuickFilters.filter(
        (visit) => (visit?.mih?.candidateEmsUsers?.length ?? 0) > 0 && !isMihCompletedCase(visit),
      );
    },
    mihUnassignedCases(): TwiageCase[] {
      return this.casesAfterApplyingQuickFilters.filter(
        (visit) => (visit.mih?.candidateEmsUsers?.length ?? 0) === 0 && !isMihCompletedCase(visit),
      );
    },
    mihCompletedCases(): TwiageCase[] {
      return this.casesAfterApplyingQuickFilters.filter(isMihCompletedCase);
    },
    edCases(): TwiageCase[] {
      return this.cases.filter(
        (twiageCase) => twiageCase.type === CaseType.ems || twiageCase.type === CaseType.hospital,
      );
    },
  },
  state,
});

export const stateIdToString = (stateId: StateId): string =>
  stateId.action + (stateId.caseId ?? "") + (stateId.actionId ?? "");
